import { Injectable, inject } from "@angular/core";
import { HttpService } from "@app/core/services/http/http.service";
import { Observable, interval, Subscription, of, map, startWith, exhaustMap, tap, switchMap, distinctUntilChanged } from "rxjs";
import { RPLUS_URL } from "@app/shared/constants/url.constants";
import {
  MyReportsAdapter,
  MyReportsJobsResponseAdapter,
  MyReportsMessagesAdapter,
  JobCountAdapter,
  MyJobs,
  MyReports,
  MyMessages,
  JobCount,
  MySelectedDocuments,
  MySelectedJobs,
  MyJobsList,
  JobRecord,
  JobReadStatus,
  ReportAsTextFileAdapter,
  ReportAsTextFile,
  MyReportsJobsRequestAdapter,
  MyReportsViewDocumentAdapter
} from "@app/app-modules/my-reports/my-reports-model";
import { MessageService } from "@app/core/services/message.service";
import { NotifyService } from "@app/core/services/notify.service";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { FormService } from "@app/core/services/form.service";
import { Pagination, Sortable } from "@app/shared/models/server-pagination.model";
import { UserEventTypes, UserEventsList } from "@app/shared/models/user-events.model";
import { UserEventsService } from "@app/core/services/user-events.service";

@Injectable({
  providedIn: "root"
})
export class MyReportsService {
  private http = inject(HttpService);
  private messageService = inject(MessageService);
  private notifyService = inject(NotifyService);
  private authenticationService = inject(AuthenticationService);
  private userSettingService = inject(UserSettingsService);
  private formService = inject(FormService);
  private userEventService = inject(UserEventsService);

  private _jobCountPolling: Subscription;
  private _lastJobCount: JobCount;
  private _jobPollErrorCount: number = 0;

  /**
   * Retrieve jobs
   */
  fetchJobs(parm: MyJobs): Observable<MyJobs> {
    const requestAdapter: MyReportsJobsRequestAdapter = new MyReportsJobsRequestAdapter();
    const responseAdapter: MyReportsJobsResponseAdapter = new MyReportsJobsResponseAdapter();
    return this.http
      .addDataParams(requestAdapter.adapt(parm))
      .get<MyJobs>(RPLUS_URL.MY_REPORTS_GET_JOBS_LIST)
      .pipe(map((response: any) => responseAdapter.adapt(response)));
  }

  /**
   * Fetch all reports for job id
   */
  fetchReportsByJob(parm: MyJobsList): Observable<MyReports> {
    const reportsAdapter: MyReportsAdapter = new MyReportsAdapter();
    return this.markJobAsRead(parm).pipe(
      switchMap(() => {
        return this.http
          .addDataParams({ userJobId: parm.jobId })
          .get<MyReports>(RPLUS_URL.MY_REPORTS_GET_DOCUMENTS_LIST)
          .pipe(map((response: any) => reportsAdapter.adapt(response)));
      })
    );
  }

  /**
   * Return the file path for a text file, specific to my-reports.
   */
  fetchReportAsTextFile(parms: ReportAsTextFile) {
    const reportsAdapter: ReportAsTextFileAdapter = new ReportAsTextFileAdapter();
    return this.http
      .addDataParams(parms)
      .get<MyJobs>(RPLUS_URL.MY_REPORTS_DOWNLOAD_AS_TEXT_FILE)
      .pipe(map((response: any) => reportsAdapter.adapt(response)));
  }

  /**
   * This method will move any file to the /webdocs/ folder and return the url
   * @param file
   * @returns
   */
  viewDocument(jobId: string, file: string) {
    const reportsAdapter: MyReportsViewDocumentAdapter = new MyReportsViewDocumentAdapter();
    return this.http
      .addDataParams({ jobId, file })
      .get<MyJobs>(RPLUS_URL.MY_REPORTS_VIEW_DOCUMENT)
      .pipe(map((response: any) => reportsAdapter.adapt(response)));
  }

  /**
   * Fetch all messages for job
   */
  fetchMessagesByJob(parm: MyJobsList): Observable<MyMessages> {
    const messageAdapter: MyReportsMessagesAdapter = new MyReportsMessagesAdapter();
    return this.markJobAsRead(parm).pipe(
      switchMap(() => {
        return this.http
          .addDataParams({ userJobId: parm.jobId })
          .get<MyMessages>(RPLUS_URL.MY_REPORTS_GET_MESSAGE_LIST)
          .pipe(map((response: any) => messageAdapter.adapt(response)));
      })
    );
  }

  /**
   * Fetch job count
   */
  fetchJobCount(): Observable<JobCount> {
    const jobCountAdapter: JobCountAdapter = new JobCountAdapter();
    return this.http
      .skipLoaderHandler()
      .addDataParams({})
      .get<JobCount>(RPLUS_URL.MY_REPORTS_GET_JOB_COUNT)
      .pipe(map((response: any) => jobCountAdapter.adapt(response)));
  }

  /**
   * Delete all messages for job
   * @param parm
   */
  deleteAllMessages(parm: MyMessages) {
    return this.http
      .addDataParams({ userJobId: parm.userJobId })
      .delete(RPLUS_URL.MY_REPORTS_DELETE_MESSAGE_LIST)
      .pipe(map((response: any) => response));
  }

  /**
   * Delete selected reports
   * @param parm
   */
  deleteReports(parm: MySelectedDocuments) {
    return this.http
      .addDataParams(parm)
      .delete(RPLUS_URL.MY_REPORTS_DELETE_DOCUMENT)
      .pipe(map((response: any) => response));
  }

  /**
   * Delete selected jobs
   * @param parm
   */
  deleteJobs(parm: MySelectedJobs) {
    return this.http
      .addDataParams(parm)
      .delete(RPLUS_URL.MY_REPORTS_DELETE_JOB)
      .pipe(map((response: any) => response));
  }

  /**
   * Mark a job as read
   * @param parm
   */
  markJobAsRead(parm: MyJobsList, forceMark: boolean = false) {
    if (
      (parm.isJobNew && parm.jobUser.toUpperCase() === this.authenticationService.credentials.username.toUpperCase()) ||
      forceMark ||
      parm.jobUser.toUpperCase() === "UPLOAD"
    ) {
      parm.isJobNew = false;
      return this.http.addDataParams({ jobId: parm.jobId, mode: JobReadStatus.markAsRead }).put(RPLUS_URL.MY_REPORTS_MARK_JOB_AS_READ_OR_UNREAD, "");
    } else {
      return of("");
    }
  }

  /**
   * Mark a job as unread
   * @param parm
   */
  markJobAsUnread(parm: MyJobsList) {
    if (!parm.isJobNew) {
      parm.isJobNew = true;
      return this.http
        .addDataParams({ jobId: parm.jobId, mode: JobReadStatus.markAsUnread })
        .put(RPLUS_URL.MY_REPORTS_MARK_JOB_AS_READ_OR_UNREAD, "");
    } else {
      return of("");
    }
  }

  /**
   * Update job description and user id
   * @param parm
   */
  updateJobRecord(parm: JobRecord) {
    return this.http
      .addDataParams(parm)
      .put(RPLUS_URL.MY_REPORTS_UPDATE_JOB_RECORD, "")
      .pipe(map((response) => response as JobRecord));
  }

  /**
   * Hold a job
   * @param parm
   */
  holdJob(parm: MyJobsList) {
    return this.http.addDataParams(parm).put(RPLUS_URL.MY_REPORTS_HOLD_JOB, "");
  }

  /**
   * Release or retry a job
   * @param parm
   */
  releaseJob(parm: MyJobsList) {
    return this.http.addDataParams(parm).put(RPLUS_URL.MY_REPORTS_RELEASE_JOB, "");
  }

  /**
   * Start polling for new reports
   * Check for new reports every 5 seconds
   *
   * Optimize:
   *  1. Emit changes only on new values (distinctUntilChanged)
   *  2. Do not call web service while aleady running (exhaustMap)
   */
  startPolling() {
    this._jobCountPolling = interval(5000)
      .pipe(
        startWith(0),
        exhaustMap(() => this.fetchJobCount()),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        tap((jobCount) => this._checkForNewJobs(jobCount))
      )
      .subscribe({
        next: (count) => {
          if (this._jobPollErrorCount > 0) {
            this._jobPollErrorCount = 0;
          }
          this.messageService.sendMyReportsJobCountMessage(count);
        },
        error: () => {
          this._jobPollErrorCount++;
          this._jobCountPolling.unsubscribe();

          if (this._jobPollErrorCount <= 3) {
            this.startPolling();
          }
        }
      });
  }

  /**
   * Stop polling for new reports
   */
  stopPolling() {
    if (this._jobCountPolling) {
      this._jobCountPolling.unsubscribe();
      this._lastJobCount = undefined;
    }
    this.messageService.sendMyReportsJobCountMessage(null);
  }

  /**
   * Fetch recent reports
   * @returns
   */
  fetchRecentReports() {
    if (!this.userEventService.eventEnabled(UserEventTypes.MyReports)) {
      this.userEventService.myReportsSignal = [];
      return of([]);
    }

    return this.formService.fetchDataModel(RPLUS_URL.MY_REPORTS_GET_JOBS_LIST).pipe(
      switchMap((data) => {
        if (!data) {
          return of(null);
        }

        const dataContext: MyJobs = data.model;
        dataContext.userId.value = this.authenticationService.credentials.username;

        dataContext.pagination = {
          pageNumber: 0,
          pageSize: this.userSettingService.recentActivitySettings.recentActivityMaxItems
        } as Pagination;

        dataContext.sortable = {
          sortColumn: "",
          sortDirection: ""
        } as Sortable;

        return this.fetchJobs(dataContext);
      }),
      map((resp) => {
        const events: UserEventsList[] = [];
        if (resp) {
          resp.jobsList?.forEach((x) => {
            const event = {
              item: x.jobId,
              description: x.jobDescription
            } as UserEventsList;
            events.push(event);
          });
        }
        this.userEventService.myReportsSignal = events;
        return events;
      })
    );
  }

  get lastJobCount(): JobCount {
    return this._lastJobCount;
  }

  /**& th
   * Check for any new new jobs
   * @param jobCount
   */
  private _checkForNewJobs(jobCount: JobCount) {
    if (this.userSettingService.reportSettings.hasReportNotificaton) {
      if (jobCount.totalCompleted > this._lastJobCount?.totalCompleted) {
        this.fetchRecentReports().subscribe();
        this.notifyService
          .printer("MY_REPORTS.LBL_NOTIFY_REPORT", "MY_REPORTS.LBL_NOTIFY_REPORT_MSG")
          .onAction()
          .subscribe(() => {
            this.messageService.sendOpenMyReportsMessage();
          });
        this.messageService.sendRefreshMyReportsMessage();
      }
    }

    this._lastJobCount = jobCount;
  }
}
